// src/components/fiscal/FiscalConfigPanel.js
import React, { useState, useEffect } from "react";
import { Save, Trash2, Plus, Edit2, ShieldAlert, FileText, Settings, HelpCircle, Eye, EyeOff } from "lucide-react";
import { fiscalService } from "../../services/fiscalService";
import { pb } from "../../services/pocketbase";

export default function FiscalConfigPanel() {
  const [configs, setConfigs] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showTokens, setShowTokens] = useState({});

  const [formData, setFormData] = useState({
    id: null,
    cnpj: "",
    razao_social: "",
    inscricao_estadual: "",
    inscricao_municipal: "",
    token_producao: "",
    token_homologacao: "",
    ambiente: "homologacao",
    ncm_padrao: "71131100",
    telefone: "",
    endereco: "",
    bairro: "",
    cidade: "",
    cep: "",
    email: "",
  });

  // --- CARREGAR CONFIGURAÇÕES ---
  const loadConfigs = async () => {
    setLoading(true);
    try {
      const data = await fiscalService.fetchConfigs();
      setConfigs(data);
    } catch (e) {
      console.error(e);
      alert("Erro ao carregar configurações fiscais: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfigs();
  }, []);

  const handleEdit = (conf) => {
    setFormData({
      id: conf.id,
      cnpj: conf.cnpj || "",
      razao_social: conf.razao_social || "",
      inscricao_estadual: conf.inscricao_estadual || "",
      inscricao_municipal: conf.inscricao_municipal || "",
      token_producao: conf.token_producao || "",
      token_homologacao: conf.token_homologacao || "",
      ambiente: conf.ambiente || "homologacao",
      ncm_padrao: conf.ncm_padrao || "71131100",
      telefone: conf.telefone || "",
      endereco: conf.endereco || "",
      bairro: conf.bairro || "",
      cidade: conf.cidade || "",
      cep: conf.cep || "",
      email: conf.email || "",
      logo: conf.logo || null,
      assinatura: conf.assinatura || null,
    });
    setIsEditing(true);
  };

  const handleNew = () => {
    setFormData({
      id: null,
      cnpj: "",
      razao_social: "",
      inscricao_estadual: "",
      inscricao_municipal: "",
      token_producao: "",
      token_homologacao: "",
      ambiente: "homologacao",
      ncm_padrao: "71131100",
      telefone: "",
      endereco: "",
      bairro: "",
      cidade: "",
      cep: "",
      email: "",
      logo: null,
      assinatura: null,
    });
    setIsEditing(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Deseja realmente excluir esta configuração fiscal? Isso impedirá a emissão de notas para este CNPJ.")) return;
    try {
      await fiscalService.deleteConfig(id);
      alert("Configuração excluída com sucesso.");
      loadConfigs();
    } catch (e) {
      alert("Erro ao excluir: " + e.message);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.cnpj || !formData.razao_social || !formData.inscricao_estadual) {
      alert("CNPJ, Razão Social e Inscrição Estadual são obrigatórios.");
      return;
    }

    setLoading(true);
    try {
      const trimmedData = {
        ...formData,
        cnpj: formData.cnpj.trim(),
        razao_social: formData.razao_social.trim(),
        inscricao_estadual: formData.inscricao_estadual.trim(),
        inscricao_municipal: formData.inscricao_municipal.trim(),
        token_producao: formData.token_producao.trim(),
        token_homologacao: formData.token_homologacao.trim(),
        ncm_padrao: formData.ncm_padrao.trim(),
        telefone: formData.telefone ? formData.telefone.trim() : "",
        endereco: formData.endereco ? formData.endereco.trim() : "",
        bairro: formData.bairro ? formData.bairro.trim() : "",
        cidade: formData.cidade ? formData.cidade.trim() : "",
        cep: formData.cep ? formData.cep.trim() : "",
        email: formData.email ? formData.email.trim() : "",
      };
      await fiscalService.saveConfig(trimmedData);
      alert(formData.id ? "Configuração fiscal updated!" : "Configuração fiscal created!");
      setIsEditing(false);
      loadConfigs();
    } catch (error) {
      alert("Erro ao salvar: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleTokenVisibility = (id) => {
    setShowTokens((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden animate-fade-in">
      {/* Header */}
      <div className="p-6 border-b bg-slate-50 flex justify-between items-center flex-wrap gap-4">
        <div>
          <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <FileText size={20} className="text-blue-600" /> Gestão Fiscal (CNPJs & Emissão)
          </h3>
          <p className="text-xs text-slate-500 mt-1">
            Configure as chaves e regimes tributários para emissão de notas fiscais via Focus NFe.
          </p>
        </div>
        {!isEditing && configs.length < 2 && (
          <button
            onClick={handleNew}
            className="bg-blue-650 hover:bg-blue-700 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 shadow-md transition-colors"
          >
            <Plus size={18} /> Adicionar CNPJ Emissor
          </button>
        )}
      </div>

      {/* Caixa de Aviso de Segurança de Certificado */}
      <div className="m-6 p-4 bg-blue-50/50 border border-blue-200 rounded-xl flex items-start gap-3">
        <ShieldAlert className="text-blue-600 shrink-0 mt-0.5" size={18} />
        <div className="text-xs text-blue-800 space-y-1">
          <p className="font-bold">Hospedagem de Certificado Digital A1</p>
          <p>
            Por motivos de segurança, seus arquivos de Certificado Digital (.pfx / .p12) e suas respectivas senhas devem ser enviados diretamente através do <b>Painel da Focus NFe</b> (Minhas Empresas &gt; Anexar Certificado).
          </p>
          <p className="opacity-90 font-medium">
            Nosso sistema necessita apenas do preenchimento dos Tokens de API gerados no painel Focus para comunicar com segurança.
          </p>
        </div>
      </div>

      <div className="p-6">
        {isEditing ? (
          <form onSubmit={handleSave} className="space-y-6">
            <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-4">
              <h4 className="font-bold text-slate-700 text-xs uppercase flex items-center gap-1.5 border-b pb-2 mb-3">
                <Settings size={14} className="text-blue-600" /> Dados Básicos da Empresa Emissora
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">CNPJ</label>
                  <input
                    className="w-full p-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                    value={formData.cnpj}
                    onChange={(e) => setFormData({ ...formData, cnpj: e.target.value })}
                    required
                    placeholder="Ex: 00.000.000/0001-00"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Razão Social</label>
                  <input
                    className="w-full p-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                    value={formData.razao_social}
                    onChange={(e) => setFormData({ ...formData, razao_social: e.target.value })}
                    required
                    placeholder="Ex: Sempre Joias Ltda"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Inscrição Estadual</label>
                  <input
                    className="w-full p-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                    value={formData.inscricao_estadual}
                    onChange={(e) => setFormData({ ...formData, inscricao_estadual: e.target.value })}
                    required
                    placeholder="Ex: 123.456.789.110"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Inscrição Municipal (Opcional)</label>
                  <input
                    className="w-full p-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                    value={formData.inscricao_municipal}
                    onChange={(e) => setFormData({ ...formData, inscricao_municipal: e.target.value })}
                    placeholder="Ex: 987654-3"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">NCM Padrão das Vendas</label>
                  <input
                    className="w-full p-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none font-mono"
                    value={formData.ncm_padrao}
                    onChange={(e) => setFormData({ ...formData, ncm_padrao: e.target.value })}
                    required
                    placeholder="Ex: 71131100"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Endereço (Rua e Número)</label>
                  <input
                    className="w-full p-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                    value={formData.endereco}
                    onChange={(e) => setFormData({ ...formData, endereco: e.target.value })}
                    placeholder="Ex: Av. Paulista, 1000"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Bairro</label>
                  <input
                    className="w-full p-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                    value={formData.bairro}
                    onChange={(e) => setFormData({ ...formData, bairro: e.target.value })}
                    placeholder="Ex: Bela Vista"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Cidade</label>
                  <input
                    className="w-full p-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                    value={formData.cidade}
                    onChange={(e) => setFormData({ ...formData, cidade: e.target.value })}
                    placeholder="Ex: São Paulo - SP"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">CEP</label>
                  <input
                    className="w-full p-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none font-mono"
                    value={formData.cep}
                    onChange={(e) => setFormData({ ...formData, cep: e.target.value })}
                    placeholder="Ex: 01310-100"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Telefone da Empresa</label>
                  <input
                    className="w-full p-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                    value={formData.telefone}
                    onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                    placeholder="Ex: (11) 3333-3333"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">E-mail da Empresa</label>
                  <input
                    type="email"
                    className="w-full p-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="Ex: contato@empresa.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t pt-3 mt-3">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Logo da Empresa</label>
                  <input
                    type="file"
                    accept="image/*"
                    className="w-full p-2 border border-slate-300 rounded-lg text-sm bg-white"
                    onChange={(e) => setFormData({ ...formData, logo: e.target.files[0] || null })}
                  />
                  {formData.logo && typeof formData.logo === "string" && (
                    <span className="text-[10px] text-slate-400">Arquivo atual: {formData.logo}</span>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Assinatura Digitalizada</label>
                  <input
                    type="file"
                    accept="image/*"
                    className="w-full p-2 border border-slate-300 rounded-lg text-sm bg-white"
                    onChange={(e) => setFormData({ ...formData, assinatura: e.target.files[0] || null })}
                  />
                  {formData.assinatura && typeof formData.assinatura === "string" && (
                    <span className="text-[10px] text-slate-400">Arquivo atual: {formData.assinatura}</span>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-4">
              <h4 className="font-bold text-slate-700 text-xs uppercase flex items-center gap-1.5 border-b pb-2 mb-3">
                <HelpCircle size={14} className="text-blue-600" /> Integração com Focus NFe
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Token de Homologação (Testes)</label>
                  <input
                    type="password"
                    className="w-full p-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none font-mono"
                    value={formData.token_homologacao}
                    onChange={(e) => setFormData({ ...formData, token_homologacao: e.target.value })}
                    placeholder="Insira o Token de Testes"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Token de Produção (Vendas Reais)</label>
                  <input
                    type="password"
                    className="w-full p-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none font-mono"
                    value={formData.token_producao}
                    onChange={(e) => setFormData({ ...formData, token_producao: e.target.value })}
                    placeholder="Insira o Token de Produção"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Ambiente de Operação Ativo</label>
                <div className="flex gap-4 mt-2">
                  <label className="flex items-center gap-2 cursor-pointer text-sm font-semibold">
                    <input
                      type="radio"
                      name="ambiente"
                      value="homologacao"
                      checked={formData.ambiente === "homologacao"}
                      onChange={() => setFormData({ ...formData, ambiente: "homologacao" })}
                      className="text-blue-600 focus:ring-blue-500"
                    />
                    <span>Homologação (Testes Fictícios)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer text-sm font-semibold text-orange-600">
                    <input
                      type="radio"
                      name="ambiente"
                      value="producao"
                      checked={formData.ambiente === "producao"}
                      onChange={() => setFormData({ ...formData, ambiente: "producao" })}
                      className="text-orange-600 focus:ring-orange-500"
                    />
                    <span>Produção (Valor Fiscal Real)</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setIsEditing(false)}
                className="px-6 py-2.5 border border-slate-300 rounded-lg text-slate-650 font-bold hover:bg-slate-50 transition-colors uppercase text-xs tracking-wide"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-8 py-2.5 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-750 hover:bg-blue-700 flex items-center gap-2 shadow-lg transition-all uppercase text-xs tracking-wide disabled:bg-blue-400"
              >
                <Save size={18} /> Salvar Empresa
              </button>
            </div>
          </form>
        ) : (
          /* Listagem de CNPJs Cadastrados */
          <div className="space-y-4">
            {configs.length === 0 ? (
              <div className="text-center py-10 text-slate-400 text-sm border-2 border-dashed border-slate-200 rounded-xl">
                Nenhuma empresa emissora cadastrada. Cadastre suas credenciais para habilitar a emissão de notas fiscais.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {configs.map((conf) => (
                  <div
                    key={conf.id}
                    className="border border-slate-200 rounded-xl p-5 hover:shadow-md transition-shadow relative bg-white flex flex-col justify-between"
                  >
                    <div>
                      <div className="flex justify-between items-start mb-3">
                        <span className={`px-2 py-0.5 rounded-full text-[9px] font-black tracking-wider border uppercase ${
                          conf.ambiente === "producao" 
                            ? "bg-orange-50 text-orange-700 border-orange-200 animate-pulse"
                            : "bg-blue-50 text-blue-700 border-blue-200"
                        }`}>
                          {conf.ambiente}
                        </span>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEdit(conf)}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Editar"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(conf.id)}
                            className="p-1.5 text-red-650 hover:bg-red-50 text-red-600 rounded-lg transition-colors"
                            title="Excluir"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>

                      <h4 className="font-extrabold text-slate-800 text-base leading-tight">
                        {conf.razao_social}
                      </h4>
                      <p className="text-xs text-slate-450 font-mono text-slate-500 mt-1 font-bold">
                        CNPJ: {conf.cnpj}
                      </p>

                      <div className="mt-4 pt-3 border-t border-slate-100 text-xs text-slate-600 space-y-1.5">
                        <div className="flex justify-between">
                          <span className="font-medium text-slate-400">Inscrição Estadual:</span>
                          <span className="font-mono font-semibold">{conf.inscricao_estadual}</span>
                        </div>
                        {conf.inscricao_municipal && (
                          <div className="flex justify-between">
                            <span className="font-medium text-slate-400">Inscrição Municipal:</span>
                            <span className="font-mono font-semibold">{conf.inscricao_municipal}</span>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <span className="font-medium text-slate-400">NCM Fallback:</span>
                          <span className="font-mono font-semibold">{conf.ncm_padrao}</span>
                        </div>
                        {conf.endereco && (
                          <div className="flex justify-between gap-4">
                            <span className="font-medium text-slate-400 shrink-0">Endereço:</span>
                            <span className="text-right text-slate-700 font-semibold truncate" title={`${conf.endereco}, ${conf.bairro || ""} - ${conf.cidade || ""} - CEP: ${conf.cep || ""}`}>
                              {conf.endereco}, {conf.bairro || ""}
                            </span>
                          </div>
                        )}
                        {conf.cidade && (
                          <div className="flex justify-between">
                            <span className="font-medium text-slate-400">Cidade/CEP:</span>
                            <span className="font-semibold text-slate-700">{conf.cidade} {conf.cep ? `(CEP: ${conf.cep})` : ""}</span>
                          </div>
                        )}
                        {conf.telefone && (
                          <div className="flex justify-between">
                            <span className="font-medium text-slate-400">Telefone:</span>
                            <span className="font-semibold text-slate-700">{conf.telefone}</span>
                          </div>
                        )}
                        {conf.email && (
                          <div className="flex justify-between">
                            <span className="font-medium text-slate-400">E-mail:</span>
                            <span className="font-semibold text-slate-700">{conf.email}</span>
                          </div>
                        )}
                        {conf.logo && (
                          <div className="flex justify-between items-center border-t pt-1.5 mt-1.5">
                            <span className="font-medium text-slate-400">Logo:</span>
                            <img src={pb.files.getUrl(conf, conf.logo)} alt="Logo" className="h-6 max-w-[80px] object-contain rounded border border-slate-200" />
                          </div>
                        )}
                        {conf.assinatura && (
                          <div className="flex justify-between items-center mt-1.5">
                            <span className="font-medium text-slate-400">Assinatura:</span>
                            <img src={pb.files.getUrl(conf, conf.assinatura)} alt="Assinatura" className="h-6 max-w-[80px] object-contain rounded border border-slate-200" />
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="mt-4 pt-3 border-t border-slate-100 space-y-2">
                      <div className="flex items-center justify-between text-[11px]">
                        <span className="font-medium text-slate-400">Token Homologação:</span>
                        <div className="flex items-center gap-1.5">
                          <span className="font-mono text-slate-700 bg-slate-55 bg-slate-100 px-1.5 py-0.5 rounded">
                            {showTokens[conf.id + "_hom"] ? conf.token_homologacao : "••••••••••••••••"}
                          </span>
                          <button
                            onClick={() => toggleTokenVisibility(conf.id + "_hom")}
                            className="text-slate-400 hover:text-slate-600 p-0.5"
                          >
                            {showTokens[conf.id + "_hom"] ? <EyeOff size={12} /> : <Eye size={12} />}
                          </button>
                        </div>
                      </div>

                      <div className="flex items-center justify-between text-[11px]">
                        <span className="font-medium text-slate-400">Token Produção:</span>
                        <div className="flex items-center gap-1.5">
                          <span className="font-mono text-slate-700 bg-slate-55 bg-slate-100 px-1.5 py-0.5 rounded">
                            {showTokens[conf.id + "_prod"] ? conf.token_producao : "••••••••••••••••"}
                          </span>
                          <button
                            onClick={() => toggleTokenVisibility(conf.id + "_prod")}
                            className="text-slate-400 hover:text-slate-600 p-0.5"
                          >
                            {showTokens[conf.id + "_prod"] ? <EyeOff size={12} /> : <Eye size={12} />}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
